import { addWeeks, diffDays, weekdayOnOrAfter } from "./dates";
import { occupantOf, sortByDate } from "./sessions";
import type { ISODate, Member, Rotation, Session } from "./types";

export type RotationRowState = "completed" | "scheduled" | "next" | "queue" | "inactive";

export interface RotationRow {
  position: number;
  memberId: string;
  member?: Member;
  state: RotationRowState;
  /** Completed session in this cycle, or the scheduled one. */
  session?: Session;
}

export function currentRotation(rotations: Rotation[]): Rotation | undefined {
  return [...rotations].sort((a, b) => b.cycle - a.cycle)[0];
}

function presentedBy(s: Session, memberId: string) {
  return s.presenter.kind === "member" && s.presenter.id === memberId && s.type !== "spontaneous";
}

export function rotationRows(rotation: Rotation, members: Map<string, Member>, sessions: Session[]): RotationRow[] {
  let nextAssigned = false;
  const inCycle = sortByDate(sessions.filter((s) => diffDays(s.date, rotation.startDate) >= 0));
  return rotation.order.map((memberId, i) => {
    const member = members.get(memberId);
    const base = { position: i + 1, memberId, member };
    if (!member || !member.active) return { ...base, state: "inactive" as const };
    const done = inCycle.filter((s) => s.status === "completed" && presentedBy(s, memberId)).at(-1);
    if (done) return { ...base, state: "completed" as const, session: done };
    const scheduled = inCycle.find((s) => s.status === "scheduled" && presentedBy(s, memberId));
    if (scheduled) return { ...base, state: "scheduled" as const, session: scheduled };
    if (!nextAssigned) {
      nextAssigned = true;
      return { ...base, state: "next" as const };
    }
    return { ...base, state: "queue" as const };
  });
}

export interface CycleProgress {
  total: number;
  completed: number;
  scheduled: number;
  waiting: number;
  percent: number;
  isComplete: boolean;
}

export function cycleProgress(rows: RotationRow[]): CycleProgress {
  const active = rows.filter((r) => r.state !== "inactive");
  const completed = active.filter((r) => r.state === "completed").length;
  const scheduled = active.filter((r) => r.state === "scheduled").length;
  const waiting = active.length - completed - scheduled;
  return {
    total: active.length,
    completed,
    scheduled,
    waiting,
    percent: active.length ? Math.round((completed / active.length) * 1000) / 10 : 0,
    isComplete: active.length > 0 && completed === active.length,
  };
}

/** Members still waiting for their turn, in rotation order. */
export function waitingMembers(rows: RotationRow[]): RotationRow[] {
  return rows.filter((r) => r.state === "next" || r.state === "queue");
}

export interface FillPreviewItem {
  date: ISODate;
  memberId: string;
}

/**
 * "Isi jadwal dari rotasi": pair the next empty session days with the next members in order.
 * Starts from this week's session day.
 */
export function fillPreview(
  rows: RotationRow[],
  sessions: Session[],
  today: ISODate,
  weekday: number,
  count: number,
): FillPreviewItem[] {
  const queue = waitingMembers(rows);
  const wanted = Math.min(count, queue.length);
  const result: FillPreviewItem[] = [];
  let date = weekdayOnOrAfter(today, weekday);
  let guard = 0;
  while (result.length < wanted && guard < 200) {
    if (!occupantOf(sessions, date)) {
      result.push({ date, memberId: queue[result.length].memberId });
    }
    date = addWeeks(date, 1);
    guard += 1;
  }
  return result;
}

/** Suggestion for Input Jadwal opened from the general button (F3). */
export function rotationSuggestion(
  rows: RotationRow[] | undefined,
  sessions: Session[],
  today: ISODate,
  weekday: number,
): { date: ISODate; memberId?: string } {
  let date = weekdayOnOrAfter(today, weekday);
  let guard = 0;
  while (occupantOf(sessions, date) && guard < 200) {
    date = addWeeks(date, 1);
    guard += 1;
  }
  const next = rows ? waitingMembers(rows)[0] : undefined;
  return { date, memberId: next?.memberId };
}

export function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
